import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { OptionDto, RemoteOptions } from '@/ui/forms/types';
import { environment } from '@env/environment';
import { LookupHandler } from '../lookup-handler';
import { dateToIsoString, dateToTimeString } from '@/shared/utils/date/timezone';

// Local interface to avoid importing from feature modules and bloating initial bundle
interface YardStatusResponse {
  statusId: number;
  statusNombre?: string;
}

interface YardStatusFilter {
  creditoId?: number | null;
  vehicleId?: number | null;
  fecha?: string | Date | null;
  hora?: string | Date | null;
}

function isYardStatusFilter(value: unknown): value is YardStatusFilter {
  return typeof value === 'object' && value !== null;
}

@Injectable({ providedIn: 'root' })
export class YardStatusLookupHandler implements LookupHandler {
  private http = inject(HttpClient);

  supports(table: string): boolean {
    return table === 'yard_status_current';
  }

  handle(remote: RemoteOptions, term: string): Observable<OptionDto[]> {
    void term;
    if (!isYardStatusFilter(remote.filter)) return of([]);

    const vehicleId = remote.filter.creditoId ?? remote.filter.vehicleId;
    const fechaRaw = remote.filter.fecha;
    const horaRaw = remote.filter.hora;

    // Spec: if any filter field is missing or vehicleId is 0, do NOT lookup.
    if (!vehicleId || fechaRaw == null || horaRaw == null || horaRaw === '') return of([]);

    let params = new HttpParams();
    const fecha = fechaRaw instanceof Date ? dateToIsoString(fechaRaw) : String(fechaRaw).split('T')[0];
    params = params.set('fecha', fecha);
    const hora = horaRaw instanceof Date ? dateToTimeString(horaRaw) : String(horaRaw).substring(0, 5);
    params = params.set('hora', hora);

    return this.http.get<YardStatusResponse>(
      `${environment.api}/api/yard/status/current/${vehicleId}`,
      { params }
    ).pipe(
      map(res => res.statusId ? [{ id: res.statusId, label: res.statusNombre || 'Sin Estado' }] : []),
      catchError(() => of([]))
    );
  }
}
